/**
 * SDK の保存領域（リフレッシュトークン・アクセストークン・保留中の購入 ID）の抽象。
 *
 * WHY 非同期を許すか（2026-09-26 モバイル Phase 1）:
 *   Web では localStorage（同期）しか無いが、iOS / Android アプリではトークンを **Keychain / Keystore** に置きたい
 *   （WebView のデータはバックアップ・端末の解析・同じ WebView で動く他の Web コンテンツから読める可能性がある）。
 *   ネイティブの安全な保存は Capacitor のブリッジ越し = 必ず非同期（Promise）になる。
 *
 * WHY SDK の内部は同期のまま使うか:
 *   core.ts / store.ts / authProviders.ts は `storage.getItem()` の戻り値をその場で使う作り（複数タブの回転検知など）。
 *   全部を async に書き換えると後方互換（ゲームが渡している同期の storage・既存テスト）と差分の大きさが割に合わない。
 *   そこで **起動時（init）に 1 回だけ必要なキーを先読みしてメモリに持ち、読み出しは同期・書き込みはメモリ即時 + 裏で永続化**
 *   というキャッシュ（CachedStorage）を挟む。アプリは 1 プロセス 1 WebView なので「別タブが書き換える」は起きず、
 *   キャッシュが古くなる心配は無い。
 *
 * 同期の storage（localStorage・ゲームが渡した同期実装）は**そのまま素通し**にする。
 *   複数タブの回転検知（core.ts 冒頭）は「保存領域を読み直すと別タブの書き込みが見える」ことに依存しているので、
 *   同期の場合にキャッシュを挟むと壊れる。= 既存の挙動は 1 バイトも変えない。
 */

/** 値そのものか、その Promise（ネイティブの安全な保存は Promise を返す） */
export type MaybePromise<T> = T | Promise<T>;

/**
 * ゲーム / アプリが渡せる保存領域。localStorage はこの形を満たす（同期）。
 * ネイティブ（Keychain / Keystore）実装は Promise を返してよい — その場合 SDK は init で先読みする。
 */
export interface KumoStorage {
  getItem(key: string): MaybePromise<string | null>;
  setItem(key: string, value: string): MaybePromise<void>;
  removeItem(key: string): MaybePromise<void>;
}

/** SDK 内部が使う同期の面（CachedStorage が必ずこの形にする） */
export interface SyncStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export const isThenable = <T>(v: unknown): v is PromiseLike<T> =>
  !!v && (typeof v === 'object' || typeof v === 'function') && typeof (v as { then?: unknown }).then === 'function';

/**
 * 同期の面を提供するラッパー。
 *   mode 'passthrough' … 下の storage が同期（既定。読み書きを毎回そのまま渡す）
 *   mode 'cached'      … 下の storage が非同期。preload したキーだけをメモリに持ち、書き込みは順番を守って裏で永続化
 *
 * 判定は preload() の最初の getItem の戻り値で行う（型だけでは同期か分からないため）。
 */
export class CachedStorage implements SyncStorage {
  private mode: 'passthrough' | 'cached' = 'passthrough';
  private readonly cache = new Map<string, string | null>();
  /** 書き込みの直列化。Keychain への set → remove の順序が入れ替わると、消したはずのトークンが残る */
  private chain: Promise<void> = Promise.resolve();
  /** 最後に失敗した永続化（テスト・診断用。失敗してもゲームは止めない = メモリ上のセッションで続行できる） */
  lastError: unknown = null;

  constructor(readonly inner: KumoStorage) {}

  get isAsync() {
    return this.mode === 'cached';
  }

  /**
   * 使うキーを先読みする。非同期の storage ならここで await し、以後の getItem は同期で返せる。
   * 同期の storage なら何もしない（素通し）。何度呼んでもよい（後から増えたキーも読む）。
   */
  async preload(keys: string[]): Promise<void> {
    for (const key of keys) {
      if (this.mode === 'cached' && this.cache.has(key)) continue;
      const v = this.inner.getItem(key);
      if (isThenable<string | null>(v)) {
        this.mode = 'cached';
        this.cache.set(key, (await v) ?? null);
      } else if (this.mode === 'cached') {
        // 同じ storage が一部だけ同期で返した（実装次第）。値はそのまま使う
        this.cache.set(key, v ?? null);
      }
    }
  }

  getItem(key: string): string | null {
    if (this.mode === 'cached') {
      if (!this.cache.has(key)) {
        // preload していないキー。非同期の storage から同期では読めないので「無い」とみなす
        // （呼ぶ側が preload のキー一覧に足す必要がある。core.ts の STORAGE_SUFFIXES）
        return null;
      }
      return this.cache.get(key) ?? null;
    }
    const v = this.inner.getItem(key);
    if (isThenable(v)) {
      // passthrough のつもりが非同期だった = init() を通さずに KumoCore を直接作り、preload していない
      throw new Error('[game-backend] This storage is asynchronous; call `await core.prepareStorage()` (Kumo.init does this) before using it');
    }
    return v;
  }

  setItem(key: string, value: string): void {
    if (this.mode === 'cached') {
      this.cache.set(key, value);
      this.persist(() => this.inner.setItem(key, value));
      return;
    }
    void this.inner.setItem(key, value);
  }

  removeItem(key: string): void {
    if (this.mode === 'cached') {
      this.cache.set(key, null);
      this.persist(() => this.inner.removeItem(key));
      return;
    }
    void this.inner.removeItem(key);
  }

  /** 保留中の書き込みがすべて永続化されるまで待つ（アプリがバックグラウンドに回る直前・テスト） */
  flush(): Promise<void> {
    return this.chain;
  }

  private persist(op: () => MaybePromise<void>) {
    this.chain = this.chain.then(async () => {
      try {
        await op();
      } catch (e) {
        // Keychain が一時的に使えない（端末ロック直後など）。メモリのセッションで続け、次の書き込みで再挑戦される
        this.lastError = e;
        console.warn('[game-backend] secure storage write failed', e);
      }
    });
  }
}
